import { GraduationCap, Briefcase, Users, Globe, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    icon: GraduationCap,
    title: "Higher Education Access",
    description: "Connect with top universities and academic institutions worldwide. Access undergraduate, graduate, and doctoral programs across disciplines.",
    image: "https://images.unsplash.com/photo-1758270704025-0e1a1793e1ca?w=800",
    large: true,
  },
  {
    icon: Briefcase,
    title: "Executive Training",
    description: "Premium executive education programs for senior leaders and decision-makers seeking global perspectives.",
    image: "https://images.pexels.com/photos/6949991/pexels-photo-6949991.jpeg?auto=compress&cs=tinysrgb&w=800",
    large: false,
  },
  {
    icon: Users,
    title: "Professional Development",
    description: "Skill-building programs and certifications for career advancement in the global marketplace.",
    image: null,
    large: false,
  },
  {
    icon: Globe,
    title: "International Academic Opportunities",
    description: "Research collaborations, exchange programs, and cross-border academic partnerships for institutions.",
    image: null,
    large: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#060B19] relative overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-16"
        >
          <span className="text-sm uppercase tracking-widest font-semibold text-primary mb-4 block">
            Our Services
          </span>
          <h2
            className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-6"
            data-testid="services-heading"
          >
            Empowering Global Talent Through Education
          </h2>
          <p className="text-lg text-white/60 leading-relaxed">
            Comprehensive programs designed to connect ambitious individuals and 
            institutions with world-class educational opportunities.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          data-testid="services-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              data-testid={`service-card-${index}`}
              className={`group relative overflow-hidden rounded-2xl glass-card card-hover ${
                service.large ? "md:row-span-2 min-h-[400px]" : "min-h-[220px]"
              }`}
            >
              {/* Background Image */}
              {service.image && (
                <div className="absolute inset-0">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-[#060B19]/80 to-transparent" />
                </div>
              )}

              {/* Content */}
              <div className="relative h-full p-8 flex flex-col justify-end">
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:border-primary transition-all duration-300">
                  <service.icon className="w-7 h-7 text-primary group-hover:text-[#060B19] transition-colors duration-300" />
                </div>

                {/* Title */}
                <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-3 tracking-tight">
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-white/60 leading-relaxed text-sm md:text-base">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="absolute top-6 right-6 w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <ArrowUpRight className="w-5 h-5 text-primary" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
